import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, Film } from "lucide-react";
import api from "../../lib/api";
import { resolveImage } from "./ImageUpload";

/**
 * VideoUpload — MP4/WebM upload with inline preview.
 * Falls back to a plain URL input so external links (Vimeo direct .mp4, etc.) can be pasted.
 */
const VideoUpload = ({ value, onChange, label = "Video", testId, maxMB = 30 }) => {
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`Dosya ${maxMB}MB'dan büyük olamaz`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Video yüklendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const preview = resolveImage(value);

  return (
    <div>
      {label ? (
        <div className="text-[0.68rem] tracking-[0.24em] uppercase text-brand-medium mb-2">
          {label}
        </div>
      ) : null}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative w-full border ${
          dragging ? "border-brand-navy border-dashed" : "border-black/15"
        } bg-white`}
        data-testid={testId}
      >
        {preview ? (
          <div className="relative">
            <video
              src={preview}
              className="w-full max-h-64 object-cover bg-black"
              controls
              muted
              playsInline
            />
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute top-2 right-2 bg-white/95 p-2 border border-black/10 hover:bg-brand-navy hover:text-white transition-colors"
              aria-label="Temizle"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X size={14} />
            </button>
            <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/70 text-white text-[0.6rem] tracking-[0.2em] uppercase px-2 py-1">
              <Film size={12} strokeWidth={1.5} /> Video
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full flex flex-col items-center justify-center py-12 text-brand-medium hover:text-brand-navy transition-colors"
            data-testid={testId ? `${testId}-open` : undefined}
          >
            <UploadCloud size={24} strokeWidth={1.2} />
            <div className="mt-3 text-sm">
              {busy ? "Yükleniyor…" : "Videoyu sürükle-bırak ya da tıkla"}
            </div>
            <div className="mt-1 text-xs text-brand-medium">
              mp4 · webm · mov (max {maxMB}MB)
            </div>
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          className="hidden"
          onChange={onPick}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-brand-medium">
        <button
          type="button"
          className="underline hover:text-brand-navy"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? "Yükleniyor…" : "Farklı dosya seç"}
        </button>
        <span className="opacity-40">·</span>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="veya .mp4 / YouTube URL yapıştır"
          className="flex-1 bg-transparent border-b border-black/10 focus:border-brand-navy focus:outline-none py-1 text-xs"
          data-testid={testId ? `${testId}-url` : undefined}
        />
      </div>
    </div>
  );
};

export default VideoUpload;
